import { banco } from '../db/banco'
import type {
  Cliente, CondicaoPagamento, ItemPedido, Pedido, Produto, Representada, SubPedido,
} from '../db/tipos'
import { arredondar2, avulsasDoItem } from './precos'
import { ehMotivoDeDevolucao } from './ajustes'
import { embalagemDoItem, textoDaEmbalagem } from '../produtos/embalagem'
import type { ContatoDoRepresentante } from '../lib/contatoDoRepresentante'
import type { Empresa } from '../db/tipos'
import { buscarEmpresa } from '../empresa/empresa'

// Uma devolucao ja registrada naquele item, para virar tarja no documento.
export interface DevolucaoNoPdf {
  quantidade: number
  valor: number // sempre positivo; o documento e quem imprime o sinal
  motivo: string // codigo; o rotulo sai por rotuloDoMotivo
}

export interface LinhaItemPdf {
  item: ItemPedido
  produto: Produto
  // O que foi VENDIDO, nao o que sobrou: pedido dela em 2026-07-30, para o
  // cliente e a fabrica verem de onde veio a diferenca. Sem devolucao, e
  // igual ao item.
  quantidadeVendida: number
  // A frase que diz o que o cliente esta comprando -- "10 CX", "10 CX + 5 un",
  // "10 dz" -- em vez do numero cru que nao dizia caixa nem unidade (achado
  // dela em 08/09). Mesma funcao que a tela do pedido ja usa, para as duas
  // nunca divergirem.
  quantidadeTexto: string
  totalVendido: number
  devolucoes: DevolucaoNoPdf[]
}

export interface DadosSubPedidoPdf {
  numero: string // ex.: 2026-0142-A
  representada: Representada
  condicaoPagamento: string | null
  tabelaPreco: string | null // nome da tabela usada no sub-pedido
  codigoCliente: string | null
  subPedido: SubPedido
  // Data em que a marca caiu, do ajuste de escopo 'sub_pedido'. Nao e campo do
  // sub-pedido: mora no historico, e por isso e o unico dado NOVO deste ciclo.
  // "Esta cancelado" e "por que" nao viram campo -- ja estao em subPedido.
  dataCancelamento: string | null
  itens: LinhaItemPdf[]
}

export interface DadosPedidoPdf {
  pedido: Pedido
  cliente: Cliente
  representante: ContatoDoRepresentante
  // Spec 2026-10-06: dados da empresa do representante (null = PDF como antes).
  empresa: Empresa | null
  totalGeral: number
  subPedidos: DadosSubPedidoPdf[]
}

// O vendido e o que sobrou MAIS o que voltou -- reconstruido da soma, nao do
// "antes" da primeira linha: assim vale igual para uma devolucao ou para tres.
function montarLinha(
  item: ItemPedido,
  produto: Produto,
  devolucoes: DevolucaoNoPdf[],
): LinhaItemPdf {
  const quantidadeDevolvida = devolucoes.reduce((soma, d) => soma + d.quantidade, 0)
  const valorDevolvido = devolucoes.reduce((soma, d) => soma + d.valor, 0)
  const quantidadeVendida = arredondar2(item.quantidade + quantidadeDevolvida)
  return {
    item,
    produto,
    quantidadeVendida,
    quantidadeTexto: textoDaEmbalagem(
      quantidadeVendida, avulsasDoItem(item).quantidade, embalagemDoItem(item, produto),
    ),
    totalVendido: arredondar2(item.total_item + valorDevolvido),
    devolucoes,
  }
}

export async function montarDadosPedido(
  pedidoId: string,
  representante: ContatoDoRepresentante,
  usuarioId?: string,
): Promise<DadosPedidoPdf> {
  const pedido = await banco.pedidos.get(pedidoId)
  if (!pedido) throw new Error('Pedido não encontrado no banco local.')
  const cliente = await banco.clientes.get(pedido.cliente_id)
  if (!cliente) throw new Error('Cliente não encontrado no banco local.')

  // O cancelado ENTRA: o resumo do cliente mostra a marca que caiu, com tarja
  // (spec 2026-08-02). Quem tira o cancelado do documento da FABRICA e
  // marcasParaFabrica, em agrupamento.ts. Apagado continua fora dos dois.
  const subPedidos = await banco.sub_pedidos
    .where('pedido_id')
    .equals(pedidoId)
    .filter((s) => s.apagado_em === null)
    .sortBy('letra')

  const dados: DadosSubPedidoPdf[] = []
  let totalGeral = 0
  for (const subPedido of subPedidos) {
    const representada = await banco.representadas.get(subPedido.representada_id)
    if (!representada) continue
    const condicao: CondicaoPagamento | undefined = subPedido.condicao_pagamento_id
      ? await banco.condicoes_pagamento.get(subPedido.condicao_pagamento_id)
      : undefined
    const tabela = subPedido.tabela_preco_id
      ? await banco.tabelas_preco.get(subPedido.tabela_preco_id)
      : undefined
    const vinculo = await banco.clientes_representadas
      .where('[cliente_id+representada_id]')
      .equals([cliente.id, representada.id])
      .first()
    let itensBrutos = await banco.itens_pedido
      .where('sub_pedido_id')
      .equals(subPedido.id)
      .filter((i) => i.apagado_em === null)
      .toArray()

    // Ha dois caminhos que cancelam uma marca, e eles deixam os itens
    // diferentes: "Nota cancelada" (fechamento.ts) preserva os itens; tirar o
    // ULTIMO item (carrinho.ts) apaga o item e SO ENTAO cancela. No segundo, a
    // folha sairia carimbada e vazia -- que e o proprio bug deste ciclo.
    //
    // O resgate so vale quando nao sobrou NENHUM vivo: com um vivo, trazer os
    // apagados junto poria na folha um produto cortado semanas antes por outro
    // motivo, como se tivesse caido com o cancelamento.
    if (subPedido.status === 'cancelado' && itensBrutos.length === 0) {
      itensBrutos = await banco.itens_pedido
        .where('sub_pedido_id')
        .equals(subPedido.id)
        .toArray()
    }

    // Devolucoes daquele sub-pedido, por item. So devolucao: CORTE e o que a
    // fabrica nao mandou -- aquilo nunca foi entregue, entao nao tem por que
    // aparecer no documento como se tivesse ido e voltado.
    const devolucoesPorItem = new Map<string, DevolucaoNoPdf[]>()
    const ajustes = await banco.ajustes_pedido
      .where('sub_pedido_id')
      .equals(subPedido.id)
      .filter(
        (a) =>
          a.apagado_em === null &&
          a.escopo === 'item' &&
          a.item_pedido_id !== null &&
          ehMotivoDeDevolucao(a.motivo),
      )
      .toArray()
    for (const ajuste of ajustes) {
      const chave = ajuste.item_pedido_id
      if (chave === null) continue
      const lista = devolucoesPorItem.get(chave) ?? []
      lista.push({
        quantidade: (ajuste.quantidade_antes ?? 0) - (ajuste.quantidade_depois ?? 0),
        valor: arredondar2(ajuste.valor_antes - ajuste.valor_depois),
        motivo: ajuste.motivo,
      })
      devolucoesPorItem.set(chave, lista)
    }

    const itens: DadosSubPedidoPdf['itens'] = []
    for (const item of itensBrutos) {
      const produto = await banco.produtos.get(item.produto_id)
      if (produto) itens.push(montarLinha(item, produto, devolucoesPorItem.get(item.id) ?? []))
    }

    // Item devolvido POR INTEIRO foi apagado e sairia da folha em silencio --
    // some o produto e ninguem sabe por que. Ele volta, com a quantidade
    // vendida e a tarja da devolucao inteira.
    for (const [itemId, devolucoes] of devolucoesPorItem) {
      if (itensBrutos.some((i) => i.id === itemId)) continue
      const item = await banco.itens_pedido.get(itemId)
      if (!item) continue
      const produto = await banco.produtos.get(item.produto_id)
      if (produto) itens.push(montarLinha({ ...item, quantidade: 0, total_item: 0 }, produto, devolucoes))
    }

    let dataCancelamento: string | null = null
    if (subPedido.status === 'cancelado') {
      const cancelamentos = await banco.ajustes_pedido
        .where('sub_pedido_id')
        .equals(subPedido.id)
        .filter((a) => a.apagado_em === null && a.escopo === 'sub_pedido')
        .toArray()
      // O mais recente: cancelar duas vezes e impossivel hoje, mas o historico
      // e append-only e nada garante que continue tendo uma linha so.
      dataCancelamento = cancelamentos
        .map((a) => a.criado_em)
        .sort()
        .at(-1) ?? null
    }

    // Marca cancelada nao entra na conta: o cliente nao deve por ela. O valor
    // dela aparece riscado na secao, que e o que da sentido a tarja.
    if (subPedido.status !== 'cancelado') totalGeral += subPedido.valor_total
    dados.push({
      numero: `${pedido.numero}-${subPedido.letra}`,
      representada,
      condicaoPagamento: condicao?.descricao ?? null,
      tabelaPreco: tabela?.nome ?? null,
      codigoCliente: vinculo?.codigo_cliente_na_representada ?? null,
      subPedido,
      dataCancelamento,
      itens,
    })
  }
  const empresa = (await buscarEmpresa(usuarioId)) ?? null
  return {
    pedido, cliente, representante, empresa,
    totalGeral: arredondar2(totalGeral),
    subPedidos: dados,
  }
}
